/**
 * this simple "container image" implementation - handles default <f:containerImage /> implementation
 *
 * this implementation will render the following markup
 *
 * <noscript data-container-image="true">
 *   <picture>
 *     <source data-size="width gte 1920" width="1920" height="1080" srcset="path/to/xlarge-source.jpg" />
 *     <source data-size="width gte large" width="1024" height="600" srcset="path/to/large-source.jpg" />
 *     <source data-size="width gte medium" width="640" height="300" srcset="path/to/medium-source.jpg" />
 *     <source width="480" height="360" srcset="path/to/small.jpg" />
 *     <img width="640" height="300" src="path/to/large.jpg" loading="lazy" />
 *   </picture>
 * </noscript>
 *
 * notice the data-size attribute, which controls the container-query like behaviour
 * you can use named breakpoints or fixed pixel values as seen in the example above.
 *
 * this is very limited and needs some rules to be obeyed in order to work correctly:
 * - only picture/source syntax supported
 * - sources need to be rendered in reverse mobile-first ordering (same behaviour as in regular picture elementes)
 */
(function (context, namedBreakpoints) {
    function replaceSpecialTokens(code) {
        const replacements = {
            ' lt ': ' < ',
            ' lte ': ' <= ',
            ' eq ': ' == ',
            ' neq ': ' != ',
            ' gte ': ' >= ',
            ' gt ': ' > ',
            ' and ': ' && ',
            ' or ': ' || ',
        };
        Object.getOwnPropertyNames(replacements).forEach(token => {
            code = code.replaceAll(token, replacements[token]);
        });
        return code;
    }

    function resizeContainerImage(picture) {
        const sources = Array.from(picture.querySelectorAll('source'));
        sources.forEach(source => source.media = 'not all');
        const width = picture.parentElement.offsetWidth;
        const height = picture.parentElement.offsetHeight;
        let validSource = null;
        for (let source of sources) {
            if (typeof source['containerImageCallback'] === 'function') {
                if (source['containerImageCallback'](width, height)) {
                    validSource = source;
                    break;
                }
            } else {
                // fallback image
                validSource = source;
                break;
            }
        }
        if (validSource) {
            validSource.media = 'all';
        }
    }

    const containerImageResizeObserver = new ResizeObserver(entries => {
        entries.forEach(entry => {
            Array.from(entry.target.querySelectorAll('picture[data-container-image]')).forEach(resizeContainerImage)
        })
    });

    // @see https://ticket.pixelpoint.at/issues/33240
    // regular browsers do it with innerHTML but safari on iOS is a different story
    let accessor;
    const testNode = document.createElement('div');
    testNode.innerHTML = '<noscript><span></span></noscript>';
    const assertionNode = testNode.querySelector('noscript');
    if (assertionNode.innerHTML.indexOf('<') !== -1) {
        accessor = node => node.innerHTML;
        accessProperty = 'innerHTML';
    } else if (assertionNode.innerText.indexOf('<') !== -1) {
        accessor = node => node.innerText;
    } else if (assertionNode.textContent.indexOf('<') !== -1) {
        accessor = node => node.textContent;
    }
    /**
     * @param {HTMLElement} noscript
     * @return {?HTMLPictureElement}
     */
    function getPictureFromNoscriptElement(noscript) {
        return new DOMParser().parseFromString(accessor(noscript), 'text/html').querySelector('picture');
    }

    let processed = false;
    function process() {
        processed = true;
        Promise.all(
            // we react to inserted picture tags as well to react to node cloning
            Array.from(context.querySelectorAll(':is(noscript[data-container-image],picture[data-container-image]):not([data-cq-image-parsed])')).map(async node => {
                node.setAttribute('data-cq-image-parsed', true);
                let picture;
                if (node instanceof HTMLPictureElement) {
                    if ('containerImageCallback' in node) {
                        return true;
                    }
                    picture = node;
                } else {
                    picture = getPictureFromNoscriptElement(node);
                    if (!picture) {
                        return false;
                    }
                    picture.setAttribute('data-container-image', 'true');
                }
                const img = picture.querySelector('img');
                img.dataset.originalWidth = `${img.width || img.naturalWidth || ''}`;
                img.dataset.originalHeight = `${img.height || img.naturalHeight || ''}`;
                const sources = Array.from(picture.querySelectorAll('source'));
                sources.forEach(source => {
                    source.media = 'not all';
                    let code = source.dataset.size ?? '';
                    code = replaceSpecialTokens(code);
                    let callback = null;
                    if (code !== '') {
                        eval(`callback = function(width, height) {
                            ${Object.getOwnPropertyNames(namedBreakpoints).map(breakpointName => `const ${breakpointName} = ${namedBreakpoints[breakpointName].numberValue};`).join('\n')}
                            return ${code};
                        }`);
                    }
                    source['containerImageCallback'] = callback;
                });
                if (!(node instanceof HTMLPictureElement)) {
                    node.parentElement.replaceChild(picture, node);
                }
                resizeContainerImage(picture);
                containerImageResizeObserver.observe(picture.parentElement);
                return true;
            })
        );
    }
    const observer = new MutationObserver(process);
    observer.observe(context, {
        childList: true,
        subtree: true,
    });
    if (processed === false) {
        process();
    }
})(document, window['namedBreakpoints'] ?? {});
